import { GoogleGenerativeAI } from "@google/generative-ai";
import { StandardPinContent, ListiclePinContent, Tip, ThemeCollageTextContent, VideoScript, VideoLayout, VideoSlide, RecommendedProduct, DesignTokens } from '../types';
import { getRelevantProduct } from './productRecommenderService';

// Define SchemaType enum locally since it's not exported from the SDK
enum SchemaType {
  STRING = "STRING",
  NUMBER = "NUMBER",
  INTEGER = "INTEGER",
  BOOLEAN = "BOOLEAN",
  ARRAY = "ARRAY",
  OBJECT = "OBJECT",
}

// Generate dynamic style guidelines based on the input prompt
const generateStyleGuidelines = async (prompt: string): Promise<string> => {
  const ai = getAiInstance();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-lite',
    contents: [{
      role: 'user',
      parts: [{
        text: `Based on the following theme, create a detailed set of visual style guidelines for image generation. The style should be unique and tailored to the theme.
                
Theme: ${prompt}

Generate guidelines that include:
1. Color palette (3-5 colors)
2. Design style (e.g., modern, rustic, minimalist)
3. Lighting and mood
4. Composition and framing
5. Any specific visual elements to include or avoid

Format the response as a clear, concise set of bullet points.`
      }]
    }]
  });

  // Extract text from the response
  if (response.candidates?.[0]?.content?.parts?.[0]?.text) {
    return response.candidates[0].content.parts[0].text.trim();
  }

  // Fallback to default guidelines if we can't extract the response
  return `Style Guidelines for "${prompt}":
- Color Palette: Tailored to theme
- Design Style: Context-appropriate
- Lighting: Professional product photography
- Composition: Clean with space for text
- Aspect Ratio: 2:3 vertical`;
};

// IMPORTANT: Add your Google AI Studio API Key here.
// This key is used on the frontend for image generation and text analysis.
const GEMINI_API_KEY = "AIzaSyC7-kJMBnelxxoTfYGH43mVtvrbLANarjg"; // <-- PASTE YOUR KEY HERE
const GEMINI_API_BASE = "https://generativelanguage.googleapis.com/v1beta";

const Type = SchemaType;
const Modality = {
  TEXT: "TEXT",
  IMAGE: "IMAGE",
};

let cachedClient: GoogleGenerativeAI | null = null;
let cachedAi: ReturnType<typeof createLegacyAi> | null = null;

// Utility: Promise timeout helper
async function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return await Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`Timeout after ${ms}ms: ${label}`)), ms))
  ]);
}

/**
 * Lazily initializes and returns the GoogleGenAI instance.
 * Throws a user-friendly error if the API key is not set.
 */
function getClient(): GoogleGenerativeAI {
  if (!GEMINI_API_KEY) {
    throw new Error("Please add your Google Gemini API key to services/geminiService.ts to proceed.");
  }
  if (!cachedClient) {
    cachedClient = new GoogleGenerativeAI(GEMINI_API_KEY);
  }
  return cachedClient;
}

function createLegacyAi() {
  return {
    models: {
      generateContent: legacyGenerateContent,
      generateImages: legacyGenerateImages,
    },
  } as const;
}

function getAiInstance() {
  if (!cachedAi) {
    getClient(); // ensure API key validated
    cachedAi = createLegacyAi();
  }
  return cachedAi;
}

type LegacyContentInput = string | any[] | { role: string; parts: any[] };

function normalizeContents(contents: LegacyContentInput) {
  if (Array.isArray(contents)) {
    if (contents.length && typeof contents[0] === "object" && "role" in contents[0] && "parts" in contents[0]) {
      return contents;
    }
    return [
      {
        role: "user",
        parts: contents.map((part) => (typeof part === "string" ? { text: part } : part)),
      },
    ];
  }

  if (typeof contents === "object" && contents && "role" in contents && "parts" in contents) {
    return [contents];
  }

  const text = typeof contents === "string" ? contents : JSON.stringify(contents ?? "");
  return [{ role: "user", parts: [{ text }] }];
}

function convertConfig(config: any) {
  if (!config) return undefined;
  const generationConfig: Record<string, any> = {};

  if (config.responseMimeType) generationConfig.responseMimeType = config.responseMimeType;
  if (config.responseModalities) generationConfig.responseModalities = config.responseModalities;

  return Object.keys(generationConfig).length ? generationConfig : undefined;
}

function extractTextFromResponse(data: any): string {
  try {
    const parts = data?.candidates?.[0]?.content?.parts;
    if (!parts) return "";
    return parts
      .map((part: any) => part?.text || "")
      .join(" ")
      .trim();
  } catch (err) {
    return "";
  }
}

async function legacyGenerateContent({ model, contents, config }: any) {
  const normalizedContents = normalizeContents(contents);
  const request: any = { contents: normalizedContents };

  // Build generationConfig with all config options
  const generationConfig: any = {};
  if (config?.responseMimeType) generationConfig.responseMimeType = config.responseMimeType;
  if (config?.responseModalities) generationConfig.responseModalities = config.responseModalities;
  if (config?.responseSchema) generationConfig.responseSchema = config.responseSchema;

  if (Object.keys(generationConfig).length > 0) {
    request.generationConfig = generationConfig;
  }

  const response = await fetch(`${GEMINI_API_BASE}/models/${model}:generateContent?key=${GEMINI_API_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  const responseText = await response.text();

  if (!response.ok) {
    console.error('Gemini API Error:', responseText);
    throw new Error(`Gemini generateContent failed (${response.status}): ${responseText}`);
  }

  if (!responseText || responseText.trim() === '') {
    console.error('Empty response from Gemini API');
    throw new Error('Gemini API returned empty response');
  }

  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    console.error('Failed to parse Gemini response:', responseText);
    throw new Error(`Invalid JSON response from Gemini: ${parseError.message}`);
  }

  if (!data.text) {
    data.text = extractTextFromResponse(data);
  }
  return data;
}

import { API_BASE } from '../apiBase';

// ... (existing code)

async function legacyGenerateImages({ model, prompt, config }: any) {
  const request: any = { model, prompt, config, apiKey: GEMINI_API_KEY };

  const response = await fetch(`${API_BASE}/api/generate-image`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Gemini generateImages failed via proxy (${response.status}): ${errorText}`);
  }

  return response.json();
}

// Helper to generate images using Gemini 2.5 Flash Image (which uses generateContent endpoint)
async function generateImageWithGeminiFlash(prompt: string, aspectRatio: string = '1:1'): Promise<string | null> {
  const ai = getAiInstance();
  try {
    const response = await withTimeout(
      ai.models.generateContent({
        model: 'gemini-3.1-flash-image', // Image generation model
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        config: {
          responseModalities: [Modality.IMAGE], // Request strictly image output
          // Note: aspect ratio support depends on the model's capabilities in generateContent.
          // Often prompt engineering is needed for aspect ratio in multimodal generation if config doesn't support it directly.
        }
      }),
      60000,
      'generateImageWithGeminiFlash'
    );

    // Extract inline image data
    const candidates = response?.candidates || [];
    for (const cand of candidates) {
      const parts = cand?.content?.parts || [];
      for (const part of parts) {
        if (part?.inlineData?.data) {
          // Verify mimeType if possible, default to jpeg
          const mimeType = part.inlineData.mimeType || 'image/jpeg';
          return `data:${mimeType};base64,${part.inlineData.data}`;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Gemini Flash Image Generation failed:', error);
    throw error;
  }
}

// --- Helpers: resilient JSON parsing for model responses ---
function stripCodeFences(text: string): string {
  const trimmed = (text || '').trim();
  if (trimmed.startsWith('```')) {
    // Remove leading ```[lang]? and trailing ```
    const withoutFirst = trimmed.replace(/^```[a-zA-Z]*\n?/, '');
    const withoutLast = withoutFirst.replace(/```\s*$/, '');
    return withoutLast.trim();
  }
  return trimmed;
}

function normalizeQuotes(text: string): string {
  return text
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
}

function removeTrailingCommas(text: string): string {
  // Remove trailing commas before } or ] (best-effort; may fail on strings containing these patterns)
  return text.replace(/,(\s*[}\]])/g, '$1');
}

function extractOuterJson(text: string): string {
  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) {
    return text.slice(first, last + 1);
  }
  return text;
}

function safeParseJson<T = any>(raw: string): T {
  const attempts: string[] = [];
  attempts.push(raw);
  attempts.push(stripCodeFences(raw));
  attempts.push(removeTrailingCommas(stripCodeFences(raw)));
  attempts.push(normalizeQuotes(removeTrailingCommas(stripCodeFences(raw))));
  attempts.push(extractOuterJson(normalizeQuotes(removeTrailingCommas(stripCodeFences(raw)))));
  let lastErr: any = null;
  for (const candidate of attempts) {
    try {
      return JSON.parse(candidate);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Failed to parse JSON');
}

// Compose base style guide text for ecom previews
function composeEcomBaseStyle(styleGuide: Record<string, string> | undefined, slidePromptSeed?: string): string {
  const sg = styleGuide || {} as any;
  const guideText = [
    sg.lighting && `Lighting: ${sg.lighting}`,
    sg.colorGrading && `Color Grading: ${sg.colorGrading}`,
    sg.mood && `Mood: ${sg.mood}`,
    sg.palette && `Palette: ${sg.palette}`,
    sg.camera && `Camera: ${sg.camera}`,
    sg.composition && `Composition: ${sg.composition}`,
    sg.effects && `Effects: ${sg.effects}`,
    slidePromptSeed && `Slide Seeds: ${slidePromptSeed.slice(0, 300)}`
  ].filter(Boolean).join('\n');
  return `Global Image Style Guide (apply to ALL visuals):\n- Maintain consistent grading, lighting, and composition across slides.\n- No on-image text > 7 words. Prefer clean, product-accurate compositions.\n${guideText}`;
}

// Variety presets to reduce repetitive scenes across slides
const VARIETY_ENVIRONMENTS = [
  'minimalist living room',
  'serene bedroom',
  'refined dining room',
  'calm entryway',
  'cozy reading nook',
  'sunlit hallway',
  'home office',
  'styled console vignette',
  'soft-lit stair landing',
  'neutral studio backdrop with lifestyle props'
];
const VARIETY_CAM_ANGLES = ['eye-level', 'low angle', 'high angle', 'overhead', '45° oblique'];
const VARIETY_TIMES = ['soft morning light', 'golden hour', 'overcast diffused light', 'evening warm interior light'];
const VARIETY_COMPOSITIONS = ['rule-of-thirds', 'centered symmetry', 'leading lines', 'tight macro detail', 'negative space emphasis'];

function compactStyleGuide(original: any): Record<string, string> {
  const allowed = ['lighting', 'colorGrading', 'mood', 'palette', 'camera', 'composition', 'effects'] as const;
  const guide: Record<string, string> = {};
  for (const key of allowed) {
    let val = '';
    const raw = (original && (original as any)[key]) ?? '';
    if (typeof raw === 'string') val = raw; else if (raw != null) val = String(raw);
    val = val.replace(/\s+/g, ' ').trim();
    if (val.length > 120) val = val.slice(0, 120).trim();
    if (val) guide[key] = val;
  }
  // Enforce total ≤ 200 words across all fields by trimming the longest fields
  const wordCount = (s: string) => (s ? s.split(/\s+/).filter(Boolean).length : 0);
  const totalWords = () => Object.values(guide).reduce((acc, s) => acc + wordCount(s), 0);
  while (totalWords() > 200) {
    // find longest field and trim 10%
    let longestKey: string | null = null;
    let longestLen = -1;
    for (const [k, v] of Object.entries(guide)) {
      if (v.length > longestLen) { longestLen = v.length; longestKey = k; }
    }
    if (!longestKey) break;
    const current = guide[longestKey];
    const newLen = Math.max(40, Math.floor(current.length * 0.9));
    guide[longestKey] = current.slice(0, newLen).trim();
  }
  return guide;
}

// Generate an image using the Image Preview model from a text-only prompt
async function generateImagePreviewFromText(textPrompt: string): Promise<string | null> {
  try {
    const ai = getAiInstance();
    const response: any = await withTimeout(
      ai.models.generateContent({
        model: 'gemini-3.1-flash-image',
        contents: [{ role: 'user', parts: [{ text: textPrompt }] }],
        config: { responseModalities: [Modality.IMAGE, Modality.TEXT] }
      }),
      15000,
      'generateImagePreviewFromText'
    );
    const candidates = response?.candidates || [];
    for (const cand of candidates) {
      const parts = cand?.content?.parts || [];
      for (const part of parts) {
        if (part?.inlineData?.data) return part.inlineData.data as string;
      }
    }
    return null;
  } catch (err) {
    console.error('Gemini Image Preview (text) failed:', err);
    return null;
  }
}

// Use Gemini Image Preview model to generate an ecommerce-ready visual from a base64 input image
async function generateImagePreviewFromBase64(base64Data: string, mimeType: string, guidanceText: string): Promise<string | null> {
  try {
    const ai = getAiInstance();
    const response: any = await withTimeout(
      ai.models.generateContent({
        model: 'gemini-3.1-flash-image',
        contents: [
          {
            role: 'user',
            parts: [
              { inlineData: { data: base64Data, mimeType } },
              { text: guidanceText }
            ]
          }
        ],
        // Ask for both modalities; SDK will return image inlineData in parts
        config: {
          responseModalities: [Modality.IMAGE, Modality.TEXT]
        }
      }),
      15000,
      'generateImagePreviewFromBase64'
    );

    // Try to extract image inline data from first candidate
    const candidates = response?.candidates || [];
    for (const cand of candidates) {
      const parts = cand?.content?.parts || [];
      for (const part of parts) {
        if (part?.inlineData?.data) {
          return part.inlineData.data as string; // base64 bytes
        }
      }
    }
    return null;
  } catch (err) {
    console.error('Gemini Image Preview generation failed:', err);
    return null;
  }
}

export async function renderEcomPreviewFromProduct(
  scenePrompt: string,
  productImageUrl: string,
  styleGuide?: Record<string, string>
): Promise<string | null> {
  try {
    const fetched = await imageUrlToBase64(productImageUrl);
    const baseStyle = composeEcomBaseStyle(styleGuide);
    const guidance = `${baseStyle}\n\nScene Prompt:\n- ${scenePrompt}\n\nRender Rules:\n- Maintain global style\n- No on-image text > 7 words`.slice(0, 1400);
    const preview = await generateImagePreviewFromBase64(fetched.data, fetched.mimeType || 'image/jpeg', guidance);
    if (preview) return preview;
    // fallback to text-only
    return await generateImagePreviewFromText(`${guidance}\n\nContext: ${scenePrompt}`);
  } catch (e) {
    console.warn('renderEcomPreviewFromProduct failed', e);
    return null;
  }
}

export async function renderEcomPreviewFromPrompt(
  scenePrompt: string,
  styleGuide?: Record<string, string>
): Promise<string | null> {
  try {
    const baseStyle = composeEcomBaseStyle(styleGuide);
    const guidance = `${baseStyle}\n\nScene Prompt:\n- ${scenePrompt}\n\nRender Rules:\n- Maintain global style\n- Clean, uncluttered background\n- No on-image text > 7 words`.slice(0, 1400);
    const preview = await generateImagePreviewFromText(guidance);
    return preview;
  } catch (e) {
    console.warn('renderEcomPreviewFromPrompt failed', e);
    return null;
  }
}

// Align a long narration text to existing slides' spokenCue without dropping content
export async function alignNarrationToSlides(
  slides: any[],
  narration: string,
  onDebug?: (msg: string) => void
): Promise<any[]> {
  const ai = getAiInstance();
  const cleanText = String(narration || '').trim();
  if (!cleanText) return slides;
  try {
    const schema = {
      type: Type.OBJECT,
      properties: {
        assignments: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              index: { type: Type.NUMBER },
              spokenCue: { type: Type.STRING }
            },
            required: ["index", "spokenCue"]
          }
        }
      },
      required: ["assignments"]
    } as any;

    const prompt = `Distribute the following narration text across ${slides.length} slides.
Rules:
- Use ALL the text; do not omit, summarize, or paraphrase.
- Preserve wording and punctuation; only split into chunks.
- Keep the original order of text.
- Prefer breaking at paragraph boundaries. If a paragraph is very long, split at sentence boundaries.
- Return JSON with { assignments: [{ index, spokenCue }] } where index is 0-based slide index.
- Ensure every slide gets a spokenCue. If narration has more content than slides, continue assigning sequentially, appending to the last slides as needed so that no text is lost.
Slides count: ${slides.length}

Narration:
"""
${cleanText}
"""`;

    const resp = await ai.models.generateContent({
      model: 'gemini-2.5-flash-lite',
      contents: prompt,
      config: { responseMimeType: 'application/json', responseSchema: schema }
    });
    if (onDebug) onDebug(`🧩 Align narration →\n${resp.text}`);
    const parsed = JSON.parse(resp.text);
    const byIndex: Record<number, string> = {};
    if (Array.isArray(parsed.assignments)) {
      for (const a of parsed.assignments) {
        const i = Math.max(0, Math.min(slides.length - 1, Number(a.index)));
        const chunk = String(a.spokenCue || '');
        byIndex[i] = byIndex[i] ? `${byIndex[i]}\n\n${chunk}` : chunk;
      }
    }
    // Ensure all slides have something; if missing, leave original spokenCue
    return slides.map((s, i) => ({ ...s, spokenCue: byIndex[i] ? String(byIndex[i]) : String(s.spokenCue || '') }));
  } catch (e) {
    if (onDebug) onDebug(`⚠️ Align narration failed, falling back: ${String(e)}`);
    // Fallback: naive paragraph distribution
    const paras = cleanText.split(/\n\s*\n+/).map(p => p.trim()).filter(Boolean);
    const out = slides.map((s) => ({ ...s }));
    if (paras.length === 0) return out;
    let idx = 0;
    for (const p of paras) {
      out[idx].spokenCue = out[idx].spokenCue ? `${out[idx].spokenCue}\n\n${p}` : p;
      idx = (idx + 1) % out.length;
    }
    return out;
  }
}


function isValidHttpUrl(string: string): boolean {
  try {
    if (!string || typeof string !== 'string') {
      console.error('Invalid URL input:', string);
      return false;
    }
    const newUrl = new URL(string);
    const isValid = newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    console.log('URL validation result:', { url: string, valid: isValid, protocol: newUrl.protocol });
    return isValid;
  } catch (err) {
    console.error('URL validation error for:', string, err);
    return false;
  }
}

async function imageUrlToBase64(url: string): Promise<{ data: string, mimeType: string }> {
  if (!isValidHttpUrl(url)) {
    throw new Error(`Invalid URL provided for image fetch: "${url}"`);
  }
  // Use a CORS proxy to fetch images from external domains.
  const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;

  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 10000);
    const response = await fetch(proxyUrl, { signal: controller.signal });
    clearTimeout(t);
    if (!response.ok) {
      throw new Error(`Failed to fetch image from ${url} via proxy: ${response.statusText}`);
    }
    const blob = await response.blob();
    const mimeType = blob.type;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== 'string') {
          return reject(new Error('Failed to read file as data URL.'));
        }
        const base64String = reader.result.split(',')[1];
        resolve({ data: base64String, mimeType: mimeType || 'image/jpeg' }); // Provide fallback mimeType
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`CORS or network error fetching ${url}. In a real app, use a server-side proxy.`, error);
    throw new Error(`Could not fetch image (timeout or CORS).`);
  }
}


export async function generateImage(prompt: string, themeContext?: string): Promise<string> {
  const ai = getAiInstance();

  // Generate dynamic style guidelines based on the theme context or prompt
  const styleGuidelines = themeContext
    ? await generateStyleGuidelines(themeContext)
    : await generateStyleGuidelines(prompt);

  // Try to find a matching product to use as reference for AI generation
  let productReference: { name: string; imageUrl: string } | null = null;
  try {
    // Transform the prompt into an effective vector search query
    const searchPrompt = `Transform this into an effective product search query for home decor items:
        "${prompt}"
        
        Style Context:
        ${styleGuidelines}
        
        Focus on:
        - Specific product types (rug, pillow, throw, furniture, lighting, etc.)
        - Materials (wool, cotton, linen, jute, wood, metal)
        - Colors and patterns
        - Styles (bohemian, scandinavian, modern, rustic, etc.)
        - Room types (living room, bedroom, dining, bathroom, etc.)
        - Product attributes (handmade, organic, sustainable, etc.)
        
        Return only the search query, no additional text.`;

    // Generate a better search query using Gemini
    const searchResult = await ai.models.generateContent({
      model: 'gemini-2.5-flash-lite',
      contents: [{ role: 'user', parts: [{ text: searchPrompt }] }],
    });

    // Extract text from the response
    let searchQuery = prompt;
    if (searchResult.candidates && searchResult.candidates[0]?.content?.parts) {
      searchQuery = searchResult.candidates[0].content.parts
        .filter((part: any) => part.text)
        .map((part: any) => part.text)
        .join(' ')
        .trim();
    }

    // Try to find a matching product - but DON'T return it directly
    // Instead, use it as a reference for AI image generation
    const product = await getRelevantProduct(searchQuery);
    if (product?.imageUrl && product?.name) {
      productReference = { name: product.name, imageUrl: product.imageUrl };
      console.log(`🎨 Found product reference for AI generation: ${product.name}`);
    }
  } catch (error) {
    console.error('Error in product search:', error);
  }

  try {
    // Build a rich scene prompt for AI generation
    let imagePrompt: string;

    if (productReference) {
      // Generate a lifestyle scene featuring the product
      imagePrompt = `Create a beautiful interior/lifestyle photograph for Pinterest.

Scene Description: ${prompt}
Featured Product: ${productReference.name}

CRITICAL RULES - MUST FOLLOW:
- ABSOLUTELY NO TEXT, WORDS, LETTERS, NUMBERS, OR TYPOGRAPHY IN THE IMAGE
- DO NOT add any titles, captions, watermarks, or overlays
- DO NOT include any signage, labels, or written content
- The image must be a PURE PHOTOGRAPH with zero text elements
- Text will be added separately via HTML

Style Guidelines:
${styleGuidelines}

Technical Requirements:
- Professional interior photography with a real product in a styled room
- Beautiful natural lighting with soft shadows
- Warm, inviting atmosphere
- Clean, uncluttered composition
- Leave some space at bottom for text overlay
- Vertical Pinterest format (2:3)
- NO white background - show the product in a realistic lifestyle setting`;
    } else {
      // No product found, generate a general lifestyle scene
      imagePrompt = `Create a beautiful interior/lifestyle photograph for Pinterest.

Scene Description: ${prompt}

CRITICAL RULES - MUST FOLLOW:
- ABSOLUTELY NO TEXT, WORDS, LETTERS, NUMBERS, OR TYPOGRAPHY IN THE IMAGE
- DO NOT add any titles, captions, watermarks, or overlays
- DO NOT include any signage, labels, or written content
- The image must be a PURE PHOTOGRAPH with zero text elements
- Text will be added separately via HTML

Style Guidelines:
${styleGuidelines}

Technical Requirements:
- Professional interior photography
- Beautiful natural lighting with soft shadows
- Warm, inviting atmosphere
- Clean, uncluttered composition
- Leave some space at bottom for text overlay
- Vertical Pinterest format (2:3)
- Magazine-quality home decor styling`;
    }

    // Generate the AI image
    console.log('🎨 Generating AI lifestyle image...');
    const imageDataUrl = await generateImageWithGeminiFlash(imagePrompt, '2:3');

    if (imageDataUrl) {
      console.log('✅ AI image generated successfully');
      return imageDataUrl;
    }

    throw new Error("Image generation failed, no images returned.");
  } catch (error: any) {
    console.error('Error generating image:', error);
    throw new Error(`Failed to generate image: ${error.message}`);
  }
}

export async function generateMultipleImages(
  prompt: string,
  count: number,
  aspectRatio: string = '2:3'
): Promise<string[]> {
  const ai = getAiInstance();

  const styleGuidelines = await generateStyleGuidelines(prompt);

  const sceneVariations = [
    'morning light, soft shadows',
    'golden hour, warm tones',
    'bright and airy, high key',
    'moody and dramatic, deep shadows',
    'natural daylight, neutral tones',
    'cozy evening, warm ambient light',
    'fresh and clean, minimal styling',
    'textured and layered, rich details'
  ];

  const aspectRatioMap: Record<string, string> = {
    '1:1': '1:1',
    '2:3': '9:16',
    '4:5': '3:4',
    '4:3': '4:3',
  };
  const imagenAspectRatio = aspectRatioMap[aspectRatio] || '9:16';

  const imagePromises = Array.from({ length: Math.min(count, 8) }, async (_, index) => {
    const sceneVariation = sceneVariations[index % sceneVariations.length];

    const imagePrompt = `Create a high-quality Pinterest-style interior/lifestyle photograph:

Scene Theme: ${prompt}
Lighting/Mood: ${sceneVariation}

CRITICAL RULES - MUST FOLLOW:
- ABSOLUTELY NO TEXT, WORDS, LETTERS, NUMBERS, OR TYPOGRAPHY IN THE IMAGE
- DO NOT add any titles, captions, watermarks, or overlays
- DO NOT include any signage, labels, or written content
- The image must be a PURE PHOTOGRAPH with zero text elements
- Text will be added later via HTML overlay

Style Guidelines:
${styleGuidelines}

Technical Requirements:
- Professional interior photography
- Clean, well-lit composition with natural lighting
- Warm, inviting atmosphere
- Some negative space for text overlay later
- Vertical Pinterest format (2:3)
- Magazine-quality styling`;

    try {
      const imageDataUrl = await generateImageWithGeminiFlash(imagePrompt, imagenAspectRatio);

      if (imageDataUrl) {
        return imageDataUrl;
      }
      throw new Error(`Image ${index + 1} generation failed`);
    } catch (error) {
      console.error(`Error generating image ${index + 1}:`, error);
      throw error;
    }
  });

  const results = await Promise.all(imagePromises);
  return results;
}

export async function generateTextContent(prompt: string): Promise<Omit<StandardPinContent, 'type' | 'image'>> {
  const ai = getAiInstance();

  const styleGuidelines = await generateStyleGuidelines(prompt);
  const textPrompt = `You are a social media expert specializing in Pinterest. Based on the theme "${prompt}", create content for a Pinterest pin. 

${styleGuidelines}

Provide a short, elegant title (max 5-7 words) and a powerful, evocative on-image headline (3-7 words, ALL CAPS) that follows these style guidelines.`;

  const response = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: textPrompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: {
            type: Type.STRING,
            description: "A short, elegant title for the Pinterest pin (5-7 words)."
          },
          description: {
            type: Type.STRING,
            description: "A powerful, evocative on-image headline in all caps (3-7 words)."
          },
        },
        required: ["title", "description"]
      },
    },
  });

  try {
    const jsonText = response.text;
    const parsed = JSON.parse(jsonText);
    return {
      title: parsed.title,
      description: parsed.description,
    };
  } catch (e) {
    console.error("Failed to parse JSON response:", response.text);
    throw new Error("Failed to get valid text content from the API.");
  }
}

// Imports are already at the top of the file

export async function extractDesignStyle(base64Image: string): Promise<DesignTokens> {
  const ai = getAiInstance();

  // Clean base64 string if it has prefix
  const cleanBase64 = base64Image.replace(/^data:image\/\w+;base64,/, '');
  const mimeType = base64Image.match(/^data:(image\/\w+);base64,/)?.[1] || 'image/jpeg';

  const prompt = `Analyze this design and extract its style tokens into a JSON object.
  
  Focus on:
  1. Font Family: Classify the main typography as 'serif', 'sans-serif', 'display', or 'handwritten'.
  2. Color Palette: Extract 5 key hex codes (primary, secondary, accent, background, text).
  3. Layout: Classify as 'centered' (symmetric), 'left-aligned' (asymmetric), or 'split' (image/text distinct zones).
  4. Overlay Style: 'solid' (solid box behind text), 'gradient' (fade behind text), or 'none' (text directly on image).

  Return JSON object with this schema:
  {
    "fontFamily": "string",
    "colorPalette": { "primary": "#...", "secondary": "#...", "accent": "#...", "background": "#...", "text": "#..." },
    "layout": "string",
    "overlayStyle": "string"
  }`;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-lite", // Using Flash Lite for speed/cost on analysis
      contents: [
        {
          role: 'user',
          parts: [
            { inlineData: { data: cleanBase64, mimeType } },
            { text: prompt }
          ]
        }
      ],
      config: { responseMimeType: "application/json" }
    });

    return JSON.parse(response.text) as DesignTokens;
  } catch (e) {
    console.error("Design extraction failed:", e);
    // Fallback tokens
    return {
      fontFamily: 'sans-serif',
      colorPalette: { primary: '#333', secondary: '#555', accent: '#000', background: '#fff', text: '#000' },
      layout: 'centered',
      overlayStyle: 'none'
    };
  }
}

interface ListicleTextContent {
  title: string;
  headline: string;
  subHeadline: string;
  tips: {
    subtitle: string;
    description: string;
    image_prompt: string; // Keep specifically for grid fallback or main image inspiration
  }[];
}

export async function generateListicleContent(prompt: string, referenceImage?: string): Promise<ListiclePinContent> {
  const ai = getAiInstance();
  let designTokens: DesignTokens | undefined;

  if (referenceImage) {
    try {
      designTokens = await extractDesignStyle(referenceImage);
    } catch (e) {
      console.error("Failed to extract design tokens:", e);
    }
  }

  const styleContext = designTokens
    ? `STYLE GUIDE: The user wants a design matching these tokens: ${JSON.stringify(designTokens)}. 
       - Font style: ${designTokens.fontFamily}
       - Layout: ${designTokens.layout}
       - Colors: Use specific color names that match the palette provided.`
    : "Style: Modern, Clean, Minimalist Infographic.";

  // New prompt structure for Infographic Layout
  const textPrompt = `You are a Pinterest content strategist. For the topic "${prompt}", create content for a HIGH-QUALITY INFOGRAPHIC PIN (Vertical 2:3).
  
  ${styleContext}

  Required Content:
  1. Main Title (for metadata): Max 7 words.
  2. BIG HEADLINE (on image): 3-6 words, very punchy, all-caps.
  3. Sub-headline: 1 sentence, compelling promise or summary.
  4. List Items (5-6 items):
     - Subtitle: Max 4 words (e.g. "Lighting Hierarchy").
     - Description: Max 8 words (e.g. "Spotlight the food, dim the room").
     - Image Prompt: Description for the SINGLE MAIN BACKGROUND IMAGE. It should be a stunning, vertical composition with clear negative space for text.

  Return JSON.`;

  const textResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: textPrompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          headline: { type: Type.STRING },
          subHeadline: { type: Type.STRING },
          tips: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                subtitle: { type: Type.STRING },
                description: { type: Type.STRING },
                image_prompt: { type: Type.STRING } // This is now for the main image
              },
              required: ["subtitle", "description", "image_prompt"]
            }
          }
        },
        required: ["title", "headline", "subHeadline", "tips"]
      }
    }
  });

  const content: ListicleTextContent = JSON.parse(textResponse.text);

  // Generate SINGLE main image
  // Use the image prompt from the first tip (which should describe the main scene) or a composite of prompts? 
  // The system prompt asked for "Image Prompt" in the list items, but logically we want ONE main image.
  // Let's use the first one's prompt as the main scene description or specific instruction.
  // Actually, better to just use the first item's prompt which was instructed to be the "SINGLE MAIN BACKGROUND IMAGE".
  const mainImagePrompt = content.tips[0].image_prompt + `. Vertical 2:3 aspect ratio. Clean negative space for text overlay. High quality, photorealistic. ${styleContext}`;

  let mainImage: string | undefined;
  try {
    mainImage = await generateImageWithGeminiFlash(mainImagePrompt, '2:3') || undefined;
  } catch (e) {
    console.error("Main image generation failed:", e);
    mainImage = "https://placehold.co/600x900/png?text=Generation+Failed";
  }

  // Determine if we fall back to grid (unlikely given instructions, but good for safety)
  // For now, enforcing Infographic layout.

  return {
    type: 'listicle',
    style: 'infographic',
    title: content.title,
    headline: content.headline,
    subHeadline: content.subHeadline,
    backgroundImage: mainImage,
    tips: content.tips.map(t => ({ subtitle: t.subtitle, description: t.description })), // No individual illustrations needed
    designTokens: designTokens
  };
}


export async function generateCarouselContent(
  prompt: string,
  uploadedImages: string[],
  isHtmlMode: boolean = false,
  htmlContext?: string
): Promise<{ title: string; slideTexts: string[]; slideDescriptions: string[] }> {
  const ai = getAiInstance();
  const slideCount = uploadedImages.length;

  // Build the content parts with all images and prompt
  const contentParts: any[] = [
    {
      text: isHtmlMode
        ? `You are an expert interior designer and social media content creator for Pinterest.

CONTEXT:
- Overall theme/prompt: "${prompt}"
- Number of slides: ${slideCount}
- You will analyze ${slideCount} images from a blog post

CRITICAL INSTRUCTIONS FOR HTML/BLOG MODE:
These images are from a blog post about interior design. For each image:

1. ANALYZE THE IMAGE: Look at what's actually shown in the photo

2. CREATE BRIEF CONCEPT TEXT (MAXIMUM 8 WORDS, ALL-CAPS):
   - Be EXTREMELY concise and specific
   - Mention the actual visible element or concept
   - Examples:
     * "BRASS ACCENTS CREATE RHYTHM"
     * "LAYERED TEXTURES ADD DEPTH"
     * "REPEATED COLORS UNIFY SPACE"
     * "SYMMETRY BRINGS BALANCE"
   - Focus on the KEY design concept the image demonstrates

3. BRIEF DESCRIPTION (1-2 sentences):
   - Explain the concept shown in the image
   - Keep it educational and concise

Create a short, elegant main title for the entire carousel (max 5-7 words).

Return a JSON object with:
- 'title': Main carousel title string
- 'slideTexts': Array of ${slideCount} brief ALL-CAPS concepts (MAX 8 WORDS each, one per image)
- 'slideDescriptions': Array of ${slideCount} brief descriptions (1-2 sentences each)

The arrays must contain exactly ${slideCount} items each.`
        : `You are an expert interior designer and social media content creator for Pinterest.

CONTEXT:
- Overall theme/prompt: "${prompt}"
- Number of slides: ${slideCount}
- You will analyze ${slideCount} images that the user has uploaded

CRITICAL INSTRUCTIONS:
Analyze each image VERY CAREFULLY and identify the SPECIFIC, VISIBLE elements in the photo. DO NOT use generic design principles or abstract concepts.

For each image:
1. IDENTIFY SPECIFICS: Look at what's actually in the image:
   - Specific materials (brass, marble, velvet, linen, oak wood, etc.)
   - Specific colors (sage green, terracotta, navy blue, etc.)
   - Specific products (pendant light, area rug, throw pillow, etc.)
   - Specific patterns (geometric, floral, striped, etc.)
   - Specific textures (woven, smooth, ribbed, etc.)

2. CREATE SMART TEXT OVERLAY (max 7 words, ALL-CAPS):
   - ✅ GOOD: "BRASS ACCENTS FOR WARMTH AND REPETITION" (mentions the actual material visible)
   - ✅ GOOD: "SAGE GREEN VELVET ADDS SOFTNESS" (specific color and material)
   - ✅ GOOD: "GEOMETRIC RUG ANCHORS THE SPACE" (specific pattern)
   - ❌ BAD: "REPEAT METALS FOR COHESION" (too generic, doesn't mention what's visible)
   - ❌ BAD: "USE TEXTURE FOR DEPTH" (abstract principle, not specific)
   
   If the image demonstrates a design principle, NAME THE SPECIFIC ELEMENT that demonstrates it.

3. DETAILED DESCRIPTION (2-3 sentences):
   - Describe what you actually see in the image
   - Mention specific materials, colors, and products
   - Explain how these specific elements work together or demonstrate a principle

Also create a short, elegant main title for the entire carousel (max 5-7 words) that ties all slides together.

Return a JSON object with:
- 'title': Main carousel title string
- 'slideTexts': Array of ${slideCount} ALL-CAPS text overlays (one per image, in order)
- 'slideDescriptions': Array of ${slideCount} detailed descriptions (one per image, in order)

The arrays must contain exactly ${slideCount} items each, matching the number of images provided.`
    }
  ];

  if (isHtmlMode && htmlContext) {
    contentParts.push({
      text: `BLOG CONTEXT (plain text, extracted from HTML):\n${htmlContext}\n\nUse this context to ensure the concepts align with the article.`
    });
  }

  // Add all images to the content
  for (let i = 0; i < uploadedImages.length; i++) {
    const imageSource = uploadedImages[i];

    contentParts.push({
      text: `\n--- IMAGE ${i + 1} of ${slideCount} ---`
    });

    // Check if it's a URL or base64
    if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
      // For URLs, convert to base64 first
      try {
        const imageData = await imageUrlToBase64(imageSource);
        contentParts.push({
          inlineData: {
            data: imageData.data,
            mimeType: imageData.mimeType
          }
        });
      } catch (error) {
        console.error(`Failed to load image ${i + 1}:`, error);
        // Skip this image if it fails to load
        continue;
      }
    } else {
      // For base64 data
      const base64Data = imageSource.split(',')[1] || imageSource;
      const mimeType = imageSource.match(/data:(image\/[a-z]+);base64,/)?.[1] || 'image/jpeg';

      contentParts.push({
        inlineData: {
          data: base64Data,
          mimeType: mimeType
        }
      });
    }
  }

  const textResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: [{
      role: 'user',
      parts: contentParts
    }],
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: {
            type: Type.STRING,
            description: "A short, elegant main title for the carousel pin."
          },
          slideTexts: {
            type: Type.ARRAY,
            description: `An array of ${slideCount} ALL-CAPS text overlays, one for each slide.`,
            items: { type: Type.STRING }
          },
          slideDescriptions: {
            type: Type.ARRAY,
            description: `An array of ${slideCount} detailed descriptions, one for each slide.`,
            items: { type: Type.STRING }
          }
        },
        required: ["title", "slideTexts", "slideDescriptions"]
      }
    }
  });

  const parsedText = JSON.parse(textResponse.text);

  if (parsedText.slideTexts.length !== slideCount) {
    throw new Error(`AI generated text for ${parsedText.slideTexts.length} slides, but ${slideCount} were expected.`);
  }

  if (parsedText.slideDescriptions.length !== slideCount) {
    throw new Error(`AI generated descriptions for ${parsedText.slideDescriptions.length} slides, but ${slideCount} were expected.`);
  }

  return {
    title: parsedText.title,
    slideTexts: parsedText.slideTexts,
    slideDescriptions: parsedText.slideDescriptions,
  };
}

// Smart Carousel Types
export interface SmartCarouselSlide {
  slideNumber: number;
  type: 'hook' | 'tip' | 'cta';
  title: string;           // Main headline (e.g., "Use Mirrors Strategically")
  subtitle?: string;       // Optional subtitle for hook slide
  tipNumber?: number;      // For tip slides: 1, 2, 3...
  bodyText: string;        // Main content text
  imagePrompt: string;     // Prompt for AI image (without text)
  image?: string;          // Generated image (base64)
}

export interface SmartCarouselContent {
  mainTitle: string;
  slideCount: number;
  colorPalette: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  slides: SmartCarouselSlide[];
  brandingUrl?: string;
}

// Generate a complete carousel from just a topic
export async function generateSmartCarousel(
  topic: string,
  slideCount: number = 6,
  brandingUrl?: string
): Promise<SmartCarouselContent> {
  const ai = getAiInstance();

  console.log(`🎠 Generating smart carousel for: "${topic}" with ${slideCount} slides`);

  // Step 1: Generate structured content plan
  const planPrompt = `You are an expert Pinterest content strategist. Create a structured carousel plan for:

TOPIC: "${topic}"
SLIDES: ${slideCount}

Create a carousel with this structure:
- Slide 1: HOOK - Eye-catching title that stops scrollers
- Slides 2 to ${slideCount - 1}: TIPS - Each tip is a specific, actionable piece of advice
- Slide ${slideCount}: CTA - Call to action (save, follow, etc.)

For each slide provide:
1. title: Short punchy headline (3-6 words)
2. tipNumber: For tip slides only (1, 2, 3...)
3. bodyText: 1-2 sentences expanding on the tip
4. imagePrompt: Description of a lifestyle/interior image that illustrates this tip (NO TEXT in the image, just the scene)

Also provide a cohesive color palette that matches the topic theme.

Return JSON with:
{
  "mainTitle": "Main carousel title",
  "colorPalette": {
    "primary": "#hex",      // Main brand color
    "secondary": "#hex",    // Supporting color
    "accent": "#hex",       // Highlight color
    "background": "#hex",   // Background/overlay color
    "text": "#hex"          // Text color
  },
  "slides": [
    {
      "slideNumber": 1,
      "type": "hook" | "tip" | "cta",
      "title": "Short Title",
      "subtitle": "Only for hook slide",
      "tipNumber": null or 1-N,
      "bodyText": "Expanded content",
      "imagePrompt": "Description for AI image generation - interior/lifestyle scene, NO TEXT"
    }
  ]
}`;

  const planResponse = await ai.models.generateContent({
    model: 'gemini-2.5-flash-lite',
    contents: planPrompt,
    config: {
      responseMimeType: 'application/json',
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          mainTitle: { type: Type.STRING },
          colorPalette: {
            type: Type.OBJECT,
            properties: {
              primary: { type: Type.STRING },
              secondary: { type: Type.STRING },
              accent: { type: Type.STRING },
              background: { type: Type.STRING },
              text: { type: Type.STRING }
            },
            required: ['primary', 'secondary', 'accent', 'background', 'text']
          },
          slides: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                slideNumber: { type: Type.NUMBER },
                type: { type: Type.STRING, enum: ['hook', 'tip', 'cta'] },
                title: { type: Type.STRING },
                subtitle: { type: Type.STRING },
                tipNumber: { type: Type.NUMBER },
                bodyText: { type: Type.STRING },
                imagePrompt: { type: Type.STRING }
              },
              required: ['slideNumber', 'type', 'title', 'bodyText', 'imagePrompt']
            }
          }
        },
        required: ['mainTitle', 'colorPalette', 'slides']
      }
    }
  });

  const plan = JSON.parse(planResponse.text);
  console.log(`📋 Generated content plan with ${plan.slides.length} slides`);

  // Step 2: Generate images for each slide (in parallel)
  console.log('🎨 Generating images for all slides...');

  const imagePromises = plan.slides.map(async (slide: SmartCarouselSlide, index: number) => {
    const enhancedPrompt = `Create a beautiful interior/lifestyle photograph for Pinterest.

Scene Description: ${slide.imagePrompt}

CRITICAL RULES - MUST FOLLOW:
- ABSOLUTELY NO TEXT, WORDS, LETTERS, NUMBERS, OR TYPOGRAPHY IN THE IMAGE
- DO NOT add any titles, captions, watermarks, or overlays
- DO NOT include any signage, labels, or written content
- The image must be a PURE PHOTOGRAPH with zero text elements
- Text will be added separately via HTML, so the image itself must be completely clean

Technical Requirements:
- Professional interior photography
- Beautiful natural lighting with soft shadows
- Warm, inviting atmosphere
- Clean, uncluttered composition
- Leave some space at bottom for text overlay
- Vertical Pinterest format (2:3)
- Magazine-quality home decor styling`;

    try {
      const imageData = await generateImageWithGeminiFlash(enhancedPrompt, '2:3');
      console.log(`✅ Generated image for slide ${index + 1}`);
      return imageData;
    } catch (error) {
      console.error(`❌ Failed to generate image for slide ${index + 1}:`, error);
      return null;
    }
  });

  const images = await Promise.all(imagePromises);

  // Combine plan with images
  const slidesWithImages: SmartCarouselSlide[] = plan.slides.map((slide: SmartCarouselSlide, index: number) => ({
    ...slide,
    image: images[index] || undefined
  }));

  return {
    mainTitle: plan.mainTitle,
    slideCount: slidesWithImages.length,
    colorPalette: plan.colorPalette,
    slides: slidesWithImages,
    brandingUrl: brandingUrl
  };
}

export interface TipsSlideContent {
  title: string;
  bodyLines: string[];
  emphasisText?: string;
  position: 'top' | 'bottom' | 'center';
}

export async function generateTipsCarouselContent(
  prompt: string,
  uploadedImages: string[],
  themeDescription?: string
): Promise<{ title: string; slideTexts: string[]; slideDescriptions: string[]; slideTips: TipsSlideContent[] }> {
  const ai = getAiInstance();
  const slideCount = uploadedImages.length;

  const contentParts: any[] = [
    {
      text: `You are an expert content strategist creating a Pinterest TIPS CAROUSEL. These carousels feature inspirational/educational content with text overlays on beautiful imagery.

THEME: "${prompt}"
${themeDescription ? `CONTEXT: ${themeDescription}` : ''}
NUMBER OF SLIDES: ${slideCount}

DESIGN STYLE (based on successful Pinterest tips carousels):
- Title: Short, punchy headline (2-5 words) - will be displayed in LIME/ACCENT color
- Body Lines: 3-6 short, impactful statements - will be WHITE text
- Emphasis Text: Optional italic text for questions or emotional hooks
- Position: Where text appears on image (top, bottom, or center)

FOR EACH SLIDE, ANALYZE THE IMAGE AND CREATE:

1. TITLE (2-5 words, Title Case):
   - Punchy, memorable headline
   - Examples: "Get to Know Yourself", "Become The Niche", "Stop Comparing Yourself"

2. BODY LINES (array of 2-6 short statements):
   - Each line should be impactful and concise
   - Mix of questions, statements, and calls to action
   - Examples:
     * "Who are you at your core?"
     * "Not your title."
     * "Not your niche."
     * "Not your expertise."

3. EMPHASIS TEXT (optional italic text):
   - Introspective questions or emotional hooks
   - Examples: "What do you actually care about?", "What pisses you off?", "What lights you up?"

4. POSITION: Where the text should appear based on image composition:
   - "top" - if the main visual interest is in the lower half
   - "bottom" - if the main visual interest is in the upper half
   - "center" - for centered compositions or when overlaying the full image

Also create:
- Main CAROUSEL TITLE (5-7 words that encapsulates the series)
- Legacy slide texts (short ALL-CAPS versions for compatibility)
- Brief descriptions for accessibility

Return JSON with:
- 'title': Main carousel title
- 'slideTexts': Array of ${slideCount} ALL-CAPS short texts (legacy format)
- 'slideDescriptions': Array of ${slideCount} brief descriptions
- 'slideTips': Array of ${slideCount} tip objects with {title, bodyLines, emphasisText, position}`
    }
  ];

  // Add all images
  for (let i = 0; i < uploadedImages.length; i++) {
    const imageSource = uploadedImages[i];
    contentParts.push({ text: `\n--- SLIDE ${i + 1} of ${slideCount} ---` });

    if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
      try {
        const imageData = await imageUrlToBase64(imageSource);
        contentParts.push({
          inlineData: { data: imageData.data, mimeType: imageData.mimeType }
        });
      } catch (error) {
        console.error(`Failed to load image ${i + 1}:`, error);
        continue;
      }
    } else {
      const base64Data = imageSource.split(',')[1] || imageSource;
      const mimeType = imageSource.match(/data:(image\/[a-z]+);base64,/)?.[1] || 'image/jpeg';
      contentParts.push({
        inlineData: { data: base64Data, mimeType: mimeType }
      });
    }
  }

  const response = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: [{ role: 'user', parts: contentParts }],
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING, description: "Main carousel title" },
          slideTexts: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
            description: "Legacy ALL-CAPS texts for each slide"
          },
          slideDescriptions: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
            description: "Brief descriptions for each slide"
          },
          slideTips: {
            type: Type.ARRAY,
            description: "Structured tip content for each slide",
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING, description: "Title headline in Title Case" },
                bodyLines: {
                  type: Type.ARRAY,
                  items: { type: Type.STRING },
                  description: "Array of short body text lines"
                },
                emphasisText: { type: Type.STRING, description: "Optional italic emphasis text" },
                position: {
                  type: Type.STRING,
                  enum: ["top", "bottom", "center"],
                  description: "Text position on image"
                }
              },
              required: ["title", "bodyLines", "position"]
            }
          }
        },
        required: ["title", "slideTexts", "slideDescriptions", "slideTips"]
      }
    }
  });

  const parsed = JSON.parse(response.text);
  return {
    title: parsed.title,
    slideTexts: parsed.slideTexts,
    slideDescriptions: parsed.slideDescriptions,
    slideTips: parsed.slideTips
  };
}

export async function generateThemeCollageContent(prompt: string): Promise<{ text: ThemeCollageTextContent }> {
  const ai = getAiInstance();
  const contentPrompt = `
        You are a high-end creative director for a brand like Chairish. Based on the theme "${prompt}", generate content for a multi-slide Pinterest carousel.
        1.  preTitle: A short, chic pre-title in all caps (e.g., 'GET THE LOOK', 'THE EDIT').
        2.  title: An exciting, stylish main title in all caps that captures the theme (e.g., 'POOL PARTY', 'SUMMER EDIT').
        3.  description: An engaging, descriptive paragraph (2-3 sentences) for the cover slide.
    `;

  const textResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: contentPrompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          preTitle: { type: Type.STRING },
          title: { type: Type.STRING },
          description: { type: Type.STRING },
        },
        required: ["preTitle", "title", "description"]
      }
    }
  });

  const parsedText = JSON.parse(textResponse.text);

  return {
    text: {
      preTitle: parsedText.preTitle,
      title: parsedText.title,
      description: parsedText.description,
    },
  };
}

export async function generateTipPinContent(prompt: string): Promise<{ title: string; tipText: string; }> {
  const ai = getAiInstance();
  const contentPrompt = `
      You are a home decor expert and social media content creator.
      Based on the theme "${prompt}", generate content for a Pinterest "Tip" pin.
      Provide a short, catchy title (max 5-7 words).
      Then, provide a helpful and concise tip as 'tipText' (1-2 sentences).
      The tip should be practical and easy to understand for a general audience.
      Return a JSON object.
    `;

  const textResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: contentPrompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: {
            type: Type.STRING,
            description: "A short, catchy title for the Pinterest pin."
          },
          tipText: {
            type: Type.STRING,
            description: "A helpful and concise home decor tip (1-2 sentences)."
          }
        },
        required: ["title", "tipText"]
      }
    }
  });

  const parsedText = JSON.parse(textResponse.text);
  return {
    title: parsedText.title,
    tipText: parsedText.tipText,
  };
}


// Layout selection logic based on content type and image dimensions
export function selectOptimalLayout(contentType: string, imageAspectRatio: number, slideIndex: number, totalSlides: number): string {
  const isLeftCamera = slideIndex % 2 === 0; // Alternate left/right for variety
  const cameraPosition = isLeftCamera ? 'left' : 'right';

  switch (contentType) {
    case 'segment_title':
    case 'emphasis':
      return 'full-slides'; // Full text display for titles and emphasis
    case 'ending':
      return 'full-camera'; // Full camera for personal closing
    case 'steps':
    case 'explanation':
      // Use image dimensions to decide layout size
      if (imageAspectRatio >= 1.2) { // Landscape image
        return `1/3-cam-${cameraPosition}-text`;
      } else { // Square or portrait image
        return `50%-cam-${cameraPosition}-text`;
      }
    case 'product_showcase':
      if (imageAspectRatio >= 1.2) {
        return `1/3-cam-${cameraPosition}-image`;
      } else {
        return `50%-cam-${cameraPosition}-image`;
      }
    default:
      return `50%-cam-${cameraPosition}-text`;
  }
}

// Content type detection based on slide content
export function detectContentType(spokenCue: string, title: string | null, points: string[] | null, slideIndex: number, totalSlides: number): string {
  const lowerCue = spokenCue.toLowerCase();
  const lowerTitle = title?.toLowerCase() || '';

  // Ending detection
  if (slideIndex === totalSlides - 1 || lowerCue.includes('thank you') || lowerCue.includes('conclusion') || lowerCue.includes('wrap up')) {
    return 'ending';
  }

  // Segment title detection
  if (lowerTitle.includes('introduction') || lowerTitle.includes('overview') ||
    lowerCue.includes('let\'s talk about') || lowerCue.includes('now we\'ll cover') ||
    (slideIndex < 3 && title && !points)) {
    return 'segment_title';
  }

  // Emphasis detection
  if (lowerCue.includes('important') || lowerCue.includes('crucial') || lowerCue.includes('key point') ||
    lowerCue.includes('remember this') || lowerTitle.includes('important')) {
    return 'emphasis';
  }

  // Steps detection
  if (points && points.length > 0 || lowerCue.includes('step') || lowerCue.includes('first') ||
    lowerCue.includes('next') || lowerTitle.includes('how to')) {
    return 'steps';
  }

  // Product showcase detection
  if (lowerCue.includes('look at') || lowerCue.includes('notice') || lowerCue.includes('see how') ||
    lowerTitle.includes('product') || lowerTitle.includes('features')) {
    return 'product_showcase';
  }

  return 'explanation';
}

export async function createVideoScriptPrompt(sentence: string, slideCount: number = 10): Promise<{ prompt: string, product: RecommendedProduct }> {
  // Step 1: Get the relevant product from the new API
  console.log('🔍 Getting relevant product for sentence:', sentence);
  const product = await getRelevantProduct(sentence);
  console.log('🎯 Selected product for video:', product.name);
  console.log('🖼️ Using product image:', product.imageUrl);

  // Step 2: Construct the detailed prompt that will guide Gemini
  // Sanitize and compress overly long topic inputs so the Review Prompt stays focused
  const rawTopic = String(sentence || '').replace(/\s+/g, ' ').trim();
  const topicSnippet = rawTopic.length > 800 ? `${rawTopic.slice(0, 800)}… (trimmed)` : rawTopic;
  const detailedPrompt = `
You are an expert AI video scriptwriter specializing in product showcases and educational content. Your goal is to create a structured video script for a presentation with intelligent layout selection.

**Core Topic Summary:** Use the following concise input as the semantic basis (excess text has been trimmed):
"${topicSnippet}"

Focus the script on the given theme and maintain visual/style consistency across all slides. Do not reference any specific product identifiers in the script itself; products will be resolved per slide from image prompts.

**IMPORTANT: The system will automatically select the optimal layout based on:**
- Content type (segment titles, steps, emphasis, product showcase, ending)
- Image dimensions (square vs landscape)
- Dynamic left/right camera positioning for visual variety

**Your job is to focus on creating great content. The system will handle layout selection automatically.**

**Video Script Generation Instructions:**
1.  **Structure:** Create a video script with a clear beginning, middle, and end. Generate exactly ${slideCount} slides as requested.
2.  **Visual Priority (Required):** At least 70% of slides must be primarily visual. If something is better shown than told (e.g., materials like Tibetan wool, textures, color, weaving, usage scenes), prefer an image with minimal or no bullets.
3.  **Slide Content:** For each slide, provide:
    *   \`spokenCue\`: The exact narration the presenter should say.
    *   \`title\`: A concise title for the slide. IMPORTANT: if the slide will be displayed with any camera+image layout (e.g., \`50%-cam-left-image\`, \`50%-cam-right-image\`, \`1/3-cam-left-image\`, \`1/3-cam-right-image\`, \`pip\`, or other image-forward layouts), set \`title\` to \`null\`. Only include a title on pure text layouts such as segment headers, emphasis, or steps without an image.
    *   \`points\`: Up to 0–2 bullets max on visual slides; use more bullets only on non-visual slides where a list truly adds value. Avoid redundant bullets that an image would convey.
    *   \`useProductImage\`: Set to \`true\` for most slides to keep it visual (aim for ≥70% of slides). Use \`false\` for a minority of text-centric slides.
    *   \`imagePrompt\`: If \`useProductImage\` is true, provide a detailed scene/setting prompt (environment, lighting, mood, camera angle, composition). Avoid on-image text longer than 7 words.
    *   \`contentType\`: Specify the type of content - one of: 'segment_title', 'steps', 'explanation', 'product_showcase', 'emphasis', 'ending'
4.  **Content Flow & Sequencing:**
    *   **Introduction:** Use contentType 'segment_title' for section headers
    *   **Product Showcase / Visuals:** Favor 'product_showcase' or 'explanation' with \`useProductImage: true\` for anything that can be seen (materials, textures, colors, context scenes). Keep bullets to max 2 or none on these.
    *   **Product Benefits:** Reserve bullets for non-visual lists that truly need enumeration.
    *   **Important Points:** Use 'emphasis' for crucial information that needs full attention
    *   **Call to Action:** End with 'ending' for personal closing
5.  **Image Usage:** Target ≥70% of slides with \`useProductImage: true\`. When \`useProductImage\` is true, ensure \`title\` is \`null\`.
6.  **Global Image Style Guide (Required):** Derive a concise, consistent style guide to apply across ALL visual slides. Provide fields: \`lighting\`, \`colorGrading\`, \`mood\`, \`palette\`, \`camera\`, \`composition\`, \`effects\`. Keep each field ≤ 120 chars.
7.  **JSON Output:** **Strictly** return a JSON object adhering to the following schema:
    \`\`\`json
    {
      "slides": [
        {
          "spokenCue": "string (narration text)",
          "title": "string | null",
          "points": ["string", "string"] | null,
          "useProductImage": "boolean",
          "imagePrompt": "string | null",
          "contentType": "string (segment_title|steps|explanation|product_showcase|emphasis|ending)"
        }
      ],
      "styleGuide": {
        "lighting": "string",
        "colorGrading": "string",
        "mood": "string",
        "palette": "string",
        "camera": "string",
        "composition": "string",
        "effects": "string"
      }
    }
    \`\`\`

**Example Slide Entries:**
\`\`\`json
    {
      "spokenCue": "Let's explore the premium materials that make this rug special.",
      "title": "Premium Materials",
      "points": ["100% natural wool", "Hand-knotted craftsmanship", "Durable construction"],
      "useProductImage": true,
      "contentType": "steps"
    }
\`\`\`

\`\`\`json
    {
      "spokenCue": "Notice the intricate weaving pattern and rich texture that makes each piece unique.",
      "title": null,
      "points": null,
      "useProductImage": true,
      "imagePrompt": "Sunlit Japandi living room, soft morning light, low camera angle, rug centered on matte concrete floor, neutral palette, shallow depth of field, clean background, no on-image text > 7 words.",
      "contentType": "product_showcase"
    }
\`\`\`

\`\`\`json
    {
      "spokenCue": "Thank you for watching. I hope this helps you choose the perfect rug for your space.",
      "title": null,
      "points": null,
      "useProductImage": false,
      "contentType": "ending"
    }
\`\`\`

Now, generate the video script based on the topic and product information provided.
    `;

  console.log('🧠 Ecom Detailed Prompt →\n', detailedPrompt);
  return { prompt: detailedPrompt, product };
}

// Get image dimensions from URL (mock implementation for now)
async function getImageDimensions(imageUrl: string): Promise<{ width: number, height: number, aspectRatio: number }> {
  // For now, return mock dimensions. In production, you'd analyze the actual image
  // Most product images are square or slightly rectangular
  return {
    width: 800,
    height: 600,
    aspectRatio: 800 / 600 // 1.33 (landscape)
  };
}

// Info Video Functions (without vector search)
export async function createInfoVideoScriptPrompt(topic: string, slideCount: number = 10): Promise<string> {
  const detailedPrompt = `
You are an expert AI video scriptwriter specializing in educational and informational content. Your goal is to create a structured video script for a presentation with intelligent layout selection.

**Core Topic:** "${topic}"

**IMPORTANT: The system will automatically select the optimal layout based on:**
- Content type (segment titles, steps, emphasis, product showcase, ending)
- Image dimensions (square vs landscape)
- Dynamic left/right camera positioning for visual variety

**Your job is to focus on creating great educational content. The system will handle layout selection automatically.**

**Video Script Generation Instructions:**
1.  **Structure:** Create a video script with a clear beginning, middle, and end. Generate exactly ${slideCount} slides as requested.
2.  **Slide Content:** For each slide, provide:
    *   \`spokenCue\`: The exact narration the presenter should say.
    *   \`title\`: A concise title for the slide (use for segment titles, important points, step headers).
    *   \`points\`: An array of strings for bullet points. Use this for detailed breakdowns, lists of benefits, steps, etc. The system will reveal these one by one.
    *   \`useGeneratedImage\`: Set to \`true\` if the slide should display a generated image. Set to \`false\` for text-only slides.
    *   \`imagePrompt\`: If useGeneratedImage is true, provide a detailed prompt for AI image generation.
    *   \`contentType\`: Specify the type of content - one of: 'segment_title', 'steps', 'explanation', 'visual_showcase', 'emphasis', 'ending'
3.  **Content Flow & Sequencing:**
    *   **Introduction:** Use contentType 'segment_title' for section headers
    *   **Visual Examples:** Use 'visual_showcase' with \`useGeneratedImage: true\` to show concepts
    *   **Educational Content:** Use 'steps' or 'explanation' with bullet points for detailed information
    *   **Important Points:** Use 'emphasis' for crucial information that needs full attention
    *   **Call to Action:** End with 'ending' for personal closing
4.  **Image Usage:** Use \`useGeneratedImage: true\` strategically for slides that benefit from visual examples, diagrams, or illustrations.
5.  **JSON Output:** **Strictly** return a JSON object adhering to the following schema:
    \`\`\`json
    {
      "slides": [
        {
          "spokenCue": "string (narration text)",
          "title": "string | null",
          "points": ["string", "string"] | null,
          "useGeneratedImage": "boolean",
          "imagePrompt": "string | null",
          "contentType": "string (segment_title|steps|explanation|visual_showcase|emphasis|ending)"
        }
      ]
    }
    \`\`\`

**Example Slide Entries:**
\`\`\`json
    {
      "spokenCue": "Let's explore the key principles of effective design.",
      "title": "Design Principles",
      "points": ["Balance and symmetry", "Color theory basics", "Typography hierarchy"],
      "useGeneratedImage": true,
      "imagePrompt": "Clean, minimalist diagram showing design principles with balanced layout, color wheel, and typography examples",
      "contentType": "steps"
    }
\`\`\`

\`\`\`json
    {
      "spokenCue": "Notice how these elements work together to create visual harmony.",
      "title": null,
      "points": null,
      "useGeneratedImage": true,
      "imagePrompt": "Beautiful example of harmonious design with balanced elements, pleasing colors, and clear typography",
      "contentType": "visual_showcase"
    }
\`\`\`

\`\`\`json
    {
      "spokenCue": "Thank you for watching! I hope this helps you create better designs.",
      "title": null,
      "points": null,
      "useGeneratedImage": false,
      "imagePrompt": null,
      "contentType": "ending"
    }
\`\`\`

Now, generate the video script based on the topic provided.
    `;

  return detailedPrompt;
}

export async function executeInfoVideoScriptPrompt(
  prompt: string,
  topic: string,
  slideCount: number = 10,
  onProgress?: (u: { totalSlides: number; processed: number; imagesAttached: number }) => void,
  onDebug?: (msg: string) => void
): Promise<VideoScript> {
  const ai = getAiInstance();
  console.log('🧠 Info Detailed Prompt →\n', prompt);
  if (onDebug) onDebug(`🧠 Info Detailed Prompt →\n${prompt}`);
  // 1. Execute the provided prompt to get text analysis
  console.log('🔄 Using Gemini model: gemini-2.5-flash for info video analysis');
  const analysisResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          slides: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                spokenCue: { type: Type.STRING },
                title: { type: Type.STRING },
                points: { type: Type.ARRAY, items: { type: Type.STRING } },
                useGeneratedImage: { type: Type.BOOLEAN },
                imagePrompt: { type: Type.STRING },
                contentType: { type: Type.STRING }
              },
              required: ["spokenCue", "contentType"]
            }
          }
        },
        required: ["slides"]
      }
    }
  });

  console.log('🧠 Info Analysis JSON →\n', analysisResponse.text);
  if (onDebug) onDebug(`🧠 Info Analysis JSON →\n${analysisResponse.text}`);
  const analysisResult = JSON.parse(analysisResponse.text);
  let { slides } = analysisResult;

  // ENFORCE EXACT SLIDE COUNT: Gemini sometimes ignores the request
  if (Array.isArray(slides) && slides.length > slideCount) {
    console.warn(`⚠️ Gemini returned ${slides.length} slides but ${slideCount} were requested. Truncating to ${slideCount}.`);
    if (onDebug) onDebug(`⚠️ Gemini returned ${slides.length} slides but ${slideCount} were requested. Truncating to ${slideCount}.`);
    slides = slides.slice(0, slideCount);
  }

  const totalSlides = Array.isArray(slides) ? slides.length : 0;
  if (onProgress) onProgress({ totalSlides, processed: 0, imagesAttached: 0 });

  // 2. Get mock image dimensions for layout selection
  const imageDimensions = { width: 800, height: 600, aspectRatio: 1.33 };
  console.log('📐 Using mock image dimensions:', imageDimensions);

  // Global base style for Info images (seeded from first available imagePrompt or topic)
  const firstImagePrompt: string | undefined = Array.isArray(slides)
    ? (slides.find((s: any) => s?.imagePrompt)?.imagePrompt as string | undefined)
    : undefined;
  const infoBaseStyle = `Global Style Rules:\n- Establish consistent color grading, aesthetic, and stylistic treatment for the entire set of images.\n- Maintain identical look-and-feel (lighting, contrast, saturation) across all slides.\n- Do not add on-image text longer than 7 words. Prefer no text unless explicitly required.\n- Clean, minimal compositions that support the narration.\n${firstImagePrompt ? `\nStyle Seed (from first slide): ${firstImagePrompt}` : `\nStyle Seed (from topic): ${topic}`}`;
  console.log('🎛️ [Info] Base Style:', infoBaseStyle);
  if (onDebug) onDebug(`🎛️ [Info] Base Style:\n${infoBaseStyle}`);

  // 3. Generate images for slides that need them (sequential to allow progress/logging)
  let processed = 0;
  let images = 0;
  const processedSlides: any[] = [];
  for (let index = 0; index < slides.length; index++) {
    // Yield to the event loop every few iterations to avoid blocking the UI
    if (index % 3 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    const slide = slides[index];
    // Detect content type if not provided or use AI's suggestion
    const detected = detectContentType(
      slide.spokenCue,
      slide.title,
      slide.points,
      index,
      slides.length
    );
    const contentType = slide.contentType || detected;

    // Enforce ≥70% visual rule: prefer visuals; we may flip to visual if needed
    const minVisual = Math.ceil(0.7 * slides.length);
    const currentVisualCount = processedSlides.filter((s: any) => s.useProductImage === true).length;

    // Select optimal layout based on content type and image dimensions
    const optimalLayout = selectOptimalLayout(
      contentType,
      imageDimensions.aspectRatio,
      index,
      slides.length
    );
    console.log(
      `📋 Info Slide ${index + 1}:\n` +
      `  Inputs→ spokenCue:"${slide.spokenCue?.slice(0, 140) || ''}", title:"${slide.title || ''}", points:${Array.isArray(slide.points) ? slide.points.length : 0}, index:${index}, total:${slides.length}, aspect:${imageDimensions.aspectRatio}\n` +
      `  detectContentType→ ${contentType}\n` +
      `  selectOptimalLayout→ ${optimalLayout}`
    );
    if (onDebug) onDebug(
      `📋 Info Slide ${index + 1}:\n` +
      `  Inputs→ ${JSON.stringify({ spokenCue: slide.spokenCue, title: slide.title, points: slide.points, index, total: slides.length, aspect: imageDimensions.aspectRatio })}\n` +
      `  detectContentType→ ${contentType}\n` +
      `  selectOptimalLayout→ ${optimalLayout}`
    );

    const processedSlide: any = {
      layout: optimalLayout,
      spokenCue: slide.spokenCue,
      contentType: contentType
    };

    if (slide.title) {
      processedSlide.title = slide.title;
    }

    if (slide.useGeneratedImage && slide.imagePrompt) {
      const combinedPrompt = `${infoBaseStyle}\n\nSlide ${index + 1} Render Prompt:\n- ${slide.imagePrompt}\n\nRules:\n- Maintain global color grading, aesthetic, and style consistency.\n- No on-image text exceeding 7 words.`;
      console.log(`🎨 [Info] Image prompt (slide ${index + 1}): ${combinedPrompt}`);
      if (onDebug) onDebug(`🎨 [Info] Image prompt (slide ${index + 1}): ${combinedPrompt}`);
      try {
        const preview = await Promise.race([
          generateImagePreviewFromText(combinedPrompt),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Image generation timed out')), 45000)
          )
        ]);
        if (preview) {
          processedSlide.generatedImage = preview;
          (processedSlide as any).imagePromptFinal = combinedPrompt;
          images += 1;
          console.log(`🖼️ [Info] Preview attached (slide ${index + 1})`);
          if (onDebug) onDebug(`🖼️ [Info] Preview attached (slide ${index + 1}) bytes:${preview.length}`);
        } else {
          console.warn(`⚠️ [Info] No preview returned (slide ${index + 1})`);
          if (onDebug) onDebug(`⚠️ [Info] No preview returned (slide ${index + 1})`);
        }
      } catch (error) {
        console.error('❌ [Info] Image preview failed for slide:', error);
        if (onDebug) onDebug(`❌ [Info] Image preview failed for slide ${index + 1}: ${String(error)}`);
        // Continue without image; don't throw
      }
    }
    processed += 1;
    processedSlides.push(processedSlide);
    if (onProgress) onProgress({ totalSlides, processed, imagesAttached: images });
  }

  // 4. Construct and return final VideoScript
  return {
    title: `Info Video: ${topic}`,
    slides: processedSlides
  };
}

export async function executeVideoScriptPrompt(
  prompt: string,
  sentence: string,
  product: RecommendedProduct,
  slideCount: number = 10,
  onProgress?: (u: { totalSlides: number; processed: number; imagesAttached: number }) => void,
  onDebug?: (msg: string) => void
): Promise<VideoScript> {
  const ai = getAiInstance();
  // 1. Execute the provided prompt to get text analysis and image prompt
  console.log('🔄 Using Gemini model: gemini-2.5-flash for text analysis');
  const analysisResponse = await ai.models.generateContent({
    model: "gemini-2.5-flash-lite",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          slides: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                spokenCue: { type: Type.STRING },
                title: { type: Type.STRING },
                points: { type: Type.ARRAY, items: { type: Type.STRING } },
                useProductImage: { type: Type.BOOLEAN },
                imagePrompt: { type: Type.STRING },
                contentType: { type: Type.STRING }
              },
              required: ["spokenCue", "contentType"]
            }
          },
          styleGuide: {
            type: Type.OBJECT,
            properties: {
              lighting: { type: Type.STRING },
              colorGrading: { type: Type.STRING },
              mood: { type: Type.STRING },
              palette: { type: Type.STRING },
              camera: { type: Type.STRING },
              composition: { type: Type.STRING },
              effects: { type: Type.STRING }
            }
          }
        },
        required: ["slides", "styleGuide"]
      }
    }
  });

  const analysisPreview = analysisResponse.text.slice(0, 2000);
  console.log('🧠 Ecom Analysis JSON (preview) →\n', analysisPreview, analysisResponse.text.length > 2000 ? '\n…(truncated)' : '');
  if (onDebug) onDebug(`🧠 Ecom Analysis JSON (preview) →\n${analysisPreview}${analysisResponse.text.length > 2000 ? '\n…(truncated)' : ''}`);
  const analysisResult = JSON.parse(analysisResponse.text);
  const { slides, styleGuide, imageEditPrompt } = analysisResult; // imageEditPrompt kept for backward-compat
  const totalSlides = Array.isArray(slides) ? slides.length : 0;
  if (onProgress) onProgress({ totalSlides, processed: 0, imagesAttached: 0 });

  // Build a style seed from slide-level image prompts to ensure consistency
  const slidePromptSeed = Array.isArray(slides)
    ? (slides
      .map((s: any) => (typeof s?.imagePrompt === 'string' ? s.imagePrompt.trim() : ''))
      .filter(Boolean)
      .slice(0, 12) // cap number of prompts considered
      .join(' | ')
    )
    : '';

  // Global base style to keep grading/aesthetic/style consistent and limit text length
  const sgRaw = styleGuide || {};
  const sg = compactStyleGuide(sgRaw);
  const guideText = [
    sg.lighting && `Lighting: ${sg.lighting}`,
    sg.colorGrading && `Color Grading: ${sg.colorGrading}`,
    sg.mood && `Mood: ${sg.mood}`,
    sg.palette && `Palette: ${sg.palette}`,
    sg.camera && `Camera: ${sg.camera}`,
    sg.composition && `Composition: ${sg.composition}`,
    sg.effects && `Effects: ${sg.effects}`,
    imageEditPrompt && `Style Seed (legacy): ${imageEditPrompt}`,
    slidePromptSeed && `Slide Seeds: ${slidePromptSeed.slice(0, 300)}`
  ].filter(Boolean).join('\n');

  const baseStyle = `Global Image Style Guide (apply to ALL visuals):\n- Maintain consistent grading, lighting, and composition across slides.\n- No on-image text > 7 words. Prefer clean, product-accurate compositions.\n${guideText}`;
  console.log('🎛️ [Ecom] Base Style:', baseStyle);
  if (onDebug) onDebug(`🎛️ [Ecom] Base Style:\n${baseStyle}`);

  // 2. Use generic dimensions for early layout heuristics; per-slide images will drive previews
  const imageDimensions = { width: 800, height: 600, aspectRatio: 1.33 };
  console.log('📐 Using generic image dimensions for initial layout heuristics:', imageDimensions);
  if (onDebug) onDebug(`📐 Generic image dimensions: ${JSON.stringify(imageDimensions)}`);

  // We will generate per-slide preview images later using per-slide vector-selected product visuals

  // 4. Process slides from Gemini response and add intelligent layout selection
  let processedCount = 0;
  let imagesAttached = 0;
  const processedSlides = [] as any[];
  const minVisual = Math.ceil(0.7 * slides.length);
  for (let index = 0; index < slides.length; index++) {
    const slide = slides[index];
    // Detect content type if not provided or use AI's suggestion
    const contentType = slide.contentType || detectContentType(
      slide.spokenCue,
      slide.title,
      slide.points,
      index,
      slides.length
    );

    // Select optimal layout based on content type and image dimensions
    const optimalLayout = selectOptimalLayout(
      contentType,
      imageDimensions.aspectRatio,
      index,
      slides.length
    );

    console.log(`📋 Slide ${index + 1}: ${contentType} → ${optimalLayout}`);

    const processedSlide: any = {
      layout: optimalLayout,
      spokenCue: slide.spokenCue,
      contentType: contentType
    };

    if (slide.title) {
      processedSlide.title = slide.title;
    }

    if (slide.points && Array.isArray(slide.points)) {
      processedSlide.points = slide.points;
    }

    // Decide if this slide should be visual; flip to visual if below quota
    const currentVisualCount = processedSlides.filter((s: any) => s.useProductImage === true || s.generatedImage).length;
    let targetUseImage = (slide.useProductImage === true) || contentType === 'product_showcase';
    if (!targetUseImage && currentVisualCount < minVisual) {
      targetUseImage = true;
    }
    // If visual, trim bullets to max 2
    if (targetUseImage && Array.isArray(processedSlide.points) && processedSlide.points.length > 2) {
      processedSlide.points = processedSlide.points.slice(0, 2);
    }

    // Attach product image to slides that need it or are product_showcase
    const shouldUseImage = targetUseImage;
    if (shouldUseImage) {
      try {
        // Step A: Use slide.imagePrompt to retrieve a relevant product via vector search
        let slideProduct = null as RecommendedProduct | null;
        if (slide.imagePrompt && typeof slide.imagePrompt === 'string' && slide.imagePrompt.trim().length > 0) {
          console.log(`🧭 [Ecom] Retrieving product for slide ${index + 1} using imagePrompt as vector query`);
          if (onDebug) onDebug(`🧭 [Ecom] Vector query (slide ${index + 1}): ${slide.imagePrompt}`);
          try {
            slideProduct = await withTimeout(getRelevantProduct(slide.imagePrompt), 10000, `getRelevantProduct slide ${index + 1}`);
            if (slideProduct) {
              console.log(`✅ [Ecom] Slide ${index + 1} product: ${slideProduct.name}`);
              if (onDebug) onDebug(`✅ [Ecom] Slide ${index + 1} product: ${slideProduct.name}\nURL: ${slideProduct.imageUrl}`);
            }
          } catch (err) {
            console.warn(`⚠️ [Ecom] Vector selection failed for slide ${index + 1}, will fallback`, err);
            if (onDebug) onDebug(`⚠️ [Ecom] Vector selection failed for slide ${index + 1}: ${String(err)}`);
          }
        }

        // Step B: Determine which product image to use (slide-level only; no global fallback)
        let base64ToUse: string | null = null;
        let mimeToUse: string = 'image/jpeg';
        if (slideProduct?.imageUrl) {
          try {
            const fetched = await imageUrlToBase64(slideProduct.imageUrl);
            base64ToUse = fetched.data;
            mimeToUse = fetched.mimeType || 'image/jpeg';
            console.log(`🔗 [Ecom] Slide ${index + 1} product image URL: ${slideProduct.imageUrl}`);
            console.log(`📦 [Ecom] Slide ${index + 1} source bytes: ${base64ToUse?.length || 0}`);
            if (onDebug) onDebug(`🔗 [Ecom] Slide ${index + 1} product image URL: ${slideProduct.imageUrl}`);
            if (onDebug) onDebug(`📦 [Ecom] Slide ${index + 1} source bytes: ${base64ToUse?.length || 0}`);
          } catch (e) {
            console.warn(`⚠️ [Ecom] Slide ${index + 1} image fetch failed, no image attached`, e);
            base64ToUse = null;
          }
        }

        // Step C: Generate per-slide preview using baseStyle + slide imagePrompt/context
        if (base64ToUse) {
          // Attach the exact source used for verification (do not overwrite generatedImage)
          (processedSlide as any).sourceImage = base64ToUse;
          (processedSlide as any).sourceImageMime = mimeToUse;
          // Enhanced style guidance with specific materials, colors, and design elements
          const styleEnhancement = `\n\nStyle Guidelines:\n- Color Palette: Earthy neutrals, warm whites, soft grays, and muted pastels\n- Materials: Natural materials like linen, cotton, wool, jute, wood, and stone\n- Design Style: Scandinavian minimalism meets modern bohemian\n- Lighting: Soft, natural light with warm artificial lighting\n- Texture: Emphasize natural textures and subtle patterns\n- Mood: Cozy, inviting, and effortlessly elegant`;

          const guidance = `${baseStyle}${styleEnhancement}\n\nSlide ${index + 1} Scene Prompt:\n- ${slide.imagePrompt || [slide.title, slide.spokenCue].filter(Boolean).join(' — ')}\n\nRender Instructions:\n- Maintain global grading/aesthetic/style consistency with the described style guidelines\n- Keep true-to-life colors and proportions with a focus on natural materials\n- Clean, uncluttered background with subtle texture\n- Use soft, diffused lighting to create a warm and inviting atmosphere\n- No on-image text exceeding 7 words\n- Ensure products are shown in a realistic home environment that matches the style`.slice(0, 1400);
          console.log(`🎨 [Ecom] Preview guidance (slide ${index + 1}): ${guidance}`);
          if (onDebug) onDebug(`🎨 [Ecom] Preview guidance (slide ${index + 1}): ${guidance}`);
          const preview = await generateImagePreviewFromBase64(base64ToUse, mimeToUse, guidance);
          if (preview) {
            processedSlide.generatedImage = preview;
            (processedSlide as any).imageGuidance = guidance;
            imagesAttached += 1;
            console.log(`🖼️ [Ecom] Attached preview image for slide ${index + 1} (bytes: ${preview?.length || 0})`);
            if (onDebug) onDebug(`🖼️ [Ecom] Preview attached (slide ${index + 1}) bytes:${preview?.length || 0}`);
          }
        }
      } catch (slideErr) {
        console.error(`❌ [Ecom] Slide ${index + 1} image pipeline failed`, slideErr);
        if (onDebug) onDebug(`❌ [Ecom] Slide ${index + 1} image pipeline failed: ${String(slideErr)}`);
      }
    }
    processedCount += 1;
    console.log(
      `📋 Ecom Slide ${index + 1}:\n` +
      `  Inputs→ spokenCue:"${slide.spokenCue?.slice(0, 140) || ''}", title:"${slide.title || ''}", points:${Array.isArray(slide.points) ? slide.points.length : 0}, index:${index}, total:${slides.length}, aspect:${imageDimensions.aspectRatio}\n` +
      `  detectContentType→ ${contentType}\n` +
      `  selectOptimalLayout→ ${optimalLayout}`
    );
    if (onDebug) onDebug(
      `📋 Ecom Slide ${index + 1}:\n` +
      `  Inputs→ ${JSON.stringify({ spokenCue: slide.spokenCue, title: slide.title, points: slide.points, index, total: slides.length, aspect: imageDimensions.aspectRatio })}\n` +
      `  detectContentType→ ${contentType}\n` +
      `  selectOptimalLayout→ ${optimalLayout}`
    );
    if (onProgress) onProgress({ totalSlides, processed: processedCount, imagesAttached });
    processedSlides.push(processedSlide);
  }

  // 5. Construct and return final VideoScript with dynamic slides
  // Emit a layout usage summary for verification
  const layoutCounts: Record<string, number> = {};
  for (const s of processedSlides as any[]) {
    const key = String(s.layout || 'unknown');
    layoutCounts[key] = (layoutCounts[key] || 0) + 1;
  }
  console.log('📊 [Ecom] Layout usage summary:', layoutCounts);
  if (onDebug) onDebug(`📊 [Ecom] Layout usage summary: ${JSON.stringify(layoutCounts)}`);

  return {
    title: `Ecommerce Video: ${product.name}`,
    slides: processedSlides
  };
}

// --- V2: JSON-first generators (no image generation/processing) ---

export async function executeEcomV2ScriptJSON(
  prompt: string,
  sentence: string,
  product: RecommendedProduct,
  slideCount: number = 26,
  onDebug?: (msg: string) => void
): Promise<{ slides: any[]; styleGuide: Record<string, string> }> {
  const ai = getAiInstance();
  if (onDebug) onDebug(`🧠 [Ecom V2] Detailed Prompt →\n${prompt}`);
  // Primary attempt: strict schema
  try {
    const analysisResponse = await withTimeout(
      ai.models.generateContent({
        model: "gemini-2.5-flash-lite",
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              slides: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    spokenCue: { type: Type.STRING },
                    title: { type: Type.STRING },
                    points: { type: Type.ARRAY, items: { type: Type.STRING } },
                    useProductImage: { type: Type.BOOLEAN },
                    imagePrompt: { type: Type.STRING },
                    contentType: { type: Type.STRING }
                  },
                  required: ["spokenCue", "contentType"]
                }
              },
              styleGuide: {
                type: Type.OBJECT,
                properties: {
                  lighting: { type: Type.STRING },
                  colorGrading: { type: Type.STRING },
                  mood: { type: Type.STRING },
                  palette: { type: Type.STRING },
                  camera: { type: Type.STRING },
                  composition: { type: Type.STRING },
                  effects: { type: Type.STRING }
                },
                required: []
              }
            },
            required: ["slides", "styleGuide"]
          }
        }
      }),
      45000,
      'executeEcomV2ScriptJSON.generateContent.primary'
    );
    if (onDebug) onDebug(`🧠 [Ecom V2] Analysis JSON (primary) →\n${analysisResponse.text}`);
    const parsedPrimary = safeParseJson(analysisResponse.text);
    // Compact style guide aggressively to avoid verbose outputs
    if (parsedPrimary.styleGuide) parsedPrimary.styleGuide = compactStyleGuide(parsedPrimary.styleGuide);
    if (Array.isArray(parsedPrimary.slides) && parsedPrimary.slides.length > slideCount) parsedPrimary.slides = parsedPrimary.slides.slice(0, slideCount);
    return { slides: parsedPrimary.slides || [], styleGuide: parsedPrimary.styleGuide || {} };
  } catch (errPrimary) {
    if (onDebug) onDebug(`⚠️ [Ecom V2] Primary attempt failed: ${String(errPrimary)}`);
  }

  // Fallback 1: simpler JSON request without responseSchema
  try {
    const simplifiedPrompt = `${prompt}\n\nReturn ONLY valid JSON (no markdown fences, no commentary).`;
    const analysisResponse2 = await withTimeout(
      ai.models.generateContent({
        model: "gemini-2.5-flash-lite",
        contents: simplifiedPrompt,
        config: {
          responseMimeType: "application/json"
        }
      }),
      70000,
      'executeEcomV2ScriptJSON.generateContent.fallback1'
    );
    if (onDebug) onDebug(`🧠 [Ecom V2] Analysis JSON (fallback1) →\n${analysisResponse2.text}`);
    const parsedFb1 = safeParseJson(analysisResponse2.text);
    if (parsedFb1.styleGuide) parsedFb1.styleGuide = compactStyleGuide(parsedFb1.styleGuide);
    if (Array.isArray(parsedFb1.slides) && parsedFb1.slides.length > slideCount) parsedFb1.slides = parsedFb1.slides.slice(0, slideCount);
    return { slides: parsedFb1.slides || [], styleGuide: parsedFb1.styleGuide || {} };
  } catch (errFb1) {
    if (onDebug) onDebug(`⚠️ [Ecom V2] Fallback1 failed: ${String(errFb1)}`);
  }

  // Fallback 2: split into two smaller requests (slides then styleGuide)
  if (onDebug) onDebug('🔁 [Ecom V2] Fallback2: requesting slides and styleGuide separately');
  const slidesPrompt = `${prompt}\n\nRespond with JSON object: { "slides": [ ... ] } only. Do not include styleGuide.`;
  const stylePrompt = `${prompt}\n\nRespond with JSON object: { "styleGuide": { ... } } only. Do not include slides.`;
  let parsedSlides: any = { slides: [] };
  let parsedStyle: any = { styleGuide: {} };
  try {
    const slidesResp = await withTimeout(
      ai.models.generateContent({
        model: "gemini-2.5-flash-lite",
        contents: slidesPrompt,
        config: { responseMimeType: "application/json" }
      }),
      60000,
      'executeEcomV2ScriptJSON.generateContent.fallback2.slides'
    );
    if (onDebug) onDebug(`🧠 [Ecom V2] Slides JSON (fallback2) →\n${slidesResp.text}`);
    parsedSlides = safeParseJson(slidesResp.text);
  } catch (e2) {
    if (onDebug) onDebug(`❌ [Ecom V2] Slides request failed: ${String(e2)}`);
  }
  try {
    const styleResp = await withTimeout(
      ai.models.generateContent({
        model: "gemini-2.5-flash-lite",
        contents: stylePrompt,
        config: { responseMimeType: "application/json" }
      }),
      30000,
      'executeEcomV2ScriptJSON.generateContent.fallback2.style'
    );
    if (onDebug) onDebug(`🧠 [Ecom V2] StyleGuide JSON (fallback2) →\n${styleResp.text}`);
    parsedStyle = safeParseJson(styleResp.text);
  } catch (e3) {
    if (onDebug) onDebug(`❌ [Ecom V2] StyleGuide request failed: ${String(e3)}`);
  }
  const slidesArr = Array.isArray(parsedSlides?.slides) ? parsedSlides.slides : [];
  const clippedSlides = slidesArr.length > slideCount ? slidesArr.slice(0, slideCount) : slidesArr;
  const sg = compactStyleGuide(parsedStyle?.styleGuide || {});
  if (clippedSlides.length === 0) {
    throw new Error('AI timed out. Please try again or reduce slide count.');
  }
  return { slides: clippedSlides, styleGuide: sg };
}

export async function attachEcomV2Previews(
  slides: any[],
  styleGuide: Record<string, string> | undefined,
  onProgress?: (u: { totalSlides: number; processed: number; imagesAttached: number }) => void,
  onDebug?: (msg: string) => void
): Promise<any[]> {
  const totalSlides = Array.isArray(slides) ? slides.length : 0;
  if (onProgress) onProgress({ totalSlides, processed: 0, imagesAttached: 0 });
  const slidePromptSeed = slides.map((s: any) => (typeof s?.imagePrompt === 'string' ? s.imagePrompt.trim() : '')).filter(Boolean).slice(0, 12).join(' | ');
  const baseStyle = composeEcomBaseStyle(styleGuide, slidePromptSeed);
  let processed = 0;
  let images = 0;
  const out: any[] = [];
  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i] || {};
    const contentType = slide.contentType || detectContentType(slide.spokenCue || '', slide.title || null, slide.points || null, i, slides.length);
    const shouldUseImage = slide.useProductImage === true || contentType === 'product_showcase' || (!!slide.imagePrompt && String(slide.imagePrompt).trim().length > 0);
    const processedSlide: any = { ...slide };
    if (shouldUseImage) {
      try {
        let slideProduct: RecommendedProduct | null = null;
        if (slide.imagePrompt && typeof slide.imagePrompt === 'string') {
          try {
            slideProduct = await withTimeout(getRelevantProduct(slide.imagePrompt), 10000, `ecomV2.getRelevantProduct.slide${i + 1}`);
            if (onDebug && slideProduct) onDebug(`✅ [Ecom V2] Slide ${i + 1} product: ${slideProduct.name}`);
          } catch (e) {
            if (onDebug) onDebug(`⚠️ [Ecom V2] Vector select failed (slide ${i + 1}): ${String(e)}`);
          }
        }
        // Fallback: try using spokenCue/title to retrieve a product
        if (!slideProduct) {
          const fbQuery = [slide.title, slide.spokenCue].filter(Boolean).join(' ');
          try {
            slideProduct = await withTimeout(getRelevantProduct(fbQuery), 8000, `ecomV2.getRelevantProduct.fb.slide${i + 1}`);
          } catch { }
        }
        if (slideProduct?.imageUrl) {
          try {
            const fetched = await imageUrlToBase64(slideProduct.imageUrl);
            // Deterministic variety pick per slide index
            const env = VARIETY_ENVIRONMENTS[i % VARIETY_ENVIRONMENTS.length];
            const angle = VARIETY_CAM_ANGLES[i % VARIETY_CAM_ANGLES.length];
            const time = VARIETY_TIMES[i % VARIETY_TIMES.length];
            const comp = VARIETY_COMPOSITIONS[i % VARIETY_COMPOSITIONS.length];
            const styleEnhancement = `\n\nStyle Guidelines:\n- Color Palette: Earthy neutrals, warm whites, soft grays, muted pastels\n- Materials: Linen, cotton, wool, jute, wood, stone\n- Design Style: Scandinavian minimalism meets modern bohemian\n- Lighting: ${time}\n- Texture: Emphasize natural textures and subtle patterns\n- Mood: Cozy, inviting, effortlessly elegant`;
            const sceneDirectives = `\n\nScene Variety:\n- Environment: ${env}\n- Camera Angle: ${angle}\n- Composition: ${comp}`;
            const guidance = `${baseStyle}${styleEnhancement}${sceneDirectives}\n\nSlide ${i + 1} Scene Prompt:\n- ${slide.imagePrompt || [slide.title, slide.spokenCue].filter(Boolean).join(' — ')}\n\nRender Instructions:\n- Maintain global style consistency\n- True-to-life colors and proportions focusing on natural materials\n- Clean, uncluttered background with subtle texture\n- No on-image text exceeding 7 words`.slice(0, 1400);
            let preview = await generateImagePreviewFromBase64(fetched.data, fetched.mimeType || 'image/jpeg', guidance);
            // Fallback to text-only preview if base64 route returns nothing
            if (!preview) {
              if (onDebug) onDebug(`⚠️ [Ecom V2] Base64 preview null (slide ${i + 1}), trying text-only`);
              preview = await generateImagePreviewFromText(`${guidance}\n\nContext: ${[slide.title, slide.spokenCue].filter(Boolean).join(' — ')}`);
            }
            if (preview) {
              processedSlide.generatedImage = preview;
              (processedSlide as any).sourceImage = fetched.data;
              (processedSlide as any).sourceImageMime = fetched.mimeType || 'image/jpeg';
              (processedSlide as any).imageGuidance = guidance;
              images += 1;
            }
          } catch (imgErr) {
            if (onDebug) onDebug(`❌ [Ecom V2] Image pipeline failed (slide ${i + 1}): ${String(imgErr)}`);
          }
        } else {
          // Final fallback: try text-only generation with no product source
          try {
            const fallbackPrompt = `${baseStyle}\n\nSlide ${i + 1} Scene Prompt:\n- ${slide.imagePrompt || [slide.title, slide.spokenCue].filter(Boolean).join(' — ')}\n\nRules:\n- No on-image text > 7 words.`;
            const preview = await generateImagePreviewFromText(fallbackPrompt);
            if (preview) {
              processedSlide.generatedImage = preview;
              images += 1;
            }
          } catch { }
        }
      } catch (err) {
        if (onDebug) onDebug(`❌ [Ecom V2] Slide ${i + 1} processing error: ${String(err)}`);
      }
    }
    processed += 1;
    out.push(processedSlide);
    if (onProgress) onProgress({ totalSlides, processed, imagesAttached: images });
  }
  return out;
}

export async function executeInfoV2ScriptJSON(
  prompt: string,
  topic: string,
  slideCount: number = 12,
  onDebug?: (msg: string) => void
): Promise<{ slides: any[] }> {
  const ai = getAiInstance();
  if (onDebug) onDebug(`🧠 [Info V2] Detailed Prompt →\n${prompt}`);
  const analysisResponse = await withTimeout(
    ai.models.generateContent({
      model: "gemini-2.5-flash-lite",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            slides: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  spokenCue: { type: Type.STRING },
                  title: { type: Type.STRING },
                  points: { type: Type.ARRAY, items: { type: Type.STRING } },
                  useGeneratedImage: { type: Type.BOOLEAN },
                  imagePrompt: { type: Type.STRING },
                  contentType: { type: Type.STRING }
                },
                required: ["spokenCue", "contentType"]
              }
            }
          },
          required: ["slides"]
        }
      }
    }),
    45000,
    'executeInfoV2ScriptJSON.generateContent'
  );
  if (onDebug) onDebug(`🧠 [Info V2] Analysis JSON →\n${analysisResponse.text}`);
  let parsed: any;
  try {
    parsed = safeParseJson(analysisResponse.text);
  } catch (e) {
    if (onDebug) onDebug(`❌ [Info V2] Failed to parse JSON: ${String(e)}\nRaw (truncated to 2k):\n${String(analysisResponse.text).slice(0, 2000)}`);
    throw new Error('AI did not return valid JSON for the info script. Please try again.');
  }
  if (Array.isArray(parsed.slides)) {
    if (parsed.slides.length > slideCount) parsed.slides = parsed.slides.slice(0, slideCount);
  }
  return { slides: parsed.slides || [] };
}
